import { DateTime } from 'luxon';
import { AutocompleteOption } from 'redesign';
import { PlacementsFiltersData, PlacementsFiltersFormType } from './placements-filters.types';
import { PlacementsFilters } from './hooks/use-placements-page.types';
import { getBranchOffices, getDepartments, getJobTypes } from '../../shifts/filters/utils/utils';

export const getPlacementsFiltersDefaultValues = (
  appliedFilters: PlacementsFilters,
  filterData: PlacementsFiltersData,
  setFlexWorkerSelectOption: (value: AutocompleteOption | null) => void
): PlacementsFiltersFormType => {
  const fromDate = appliedFilters.fromDate
    ? appliedFilters.fromDate.toFormat('yyyy-MM-dd')
    : DateTime.now().toFormat('yyyy-MM-dd');
  const isFromDateCheckboxSelected = !!appliedFilters.fromDate;

  const businessUnitCheckboxValue = !!appliedFilters.company;
  const result: PlacementsFiltersFormType = {
    fromDateCheckboxValue: isFromDateCheckboxSelected,
    fromDate,
    businessUnitCheckboxValue,
    company: null,
    branchOffices: null,
    departments: null,
    jobTypeCheckboxValue: !!appliedFilters.jobType?.length,
    jobType: null,
    allowWorkersFilter: !!appliedFilters.worker,
    worker: null,
    employmentTypeCheckboxValue: (appliedFilters.isFreelance || appliedFilters.isTempWork) ?? false,
    isFreelance: appliedFilters.isFreelance ?? false,
    isTempWork: appliedFilters.isTempWork ?? false,
    state: appliedFilters.states ?? [],
    isStateFilterEnabled: !!appliedFilters.states,
    accepted: appliedFilters.states?.includes('accepted') ?? false,
    cancelled: appliedFilters.states?.includes('canceled') ?? false,
    cancelationOfferClaim: appliedFilters.states?.includes('cancelationOfferClaim') ?? false,
    cancelationOfferPending: appliedFilters.states?.includes('cancelationOfferPending') ?? false,
    counterOffer: appliedFilters.states?.includes('counterOffer') ?? false,
    noShow: appliedFilters.states?.includes('noShow') ?? false,
    checkoutPending: appliedFilters.states?.includes('checkoutPending') ?? false,
    checkoutSettled: appliedFilters.states?.includes('checkoutSettled') ?? false,
  };

  if (appliedFilters.jobType?.length) {
    const jobTypes = getJobTypes(appliedFilters.jobType, filterData.jobTypes);
    if (jobTypes?.length) {
      result.jobType = jobTypes.map((i) => ({ id: i.value, name: i.label }));
    }
  }
  const allBranchOffices = filterData.companies.flatMap((c) => c.branchOffices ?? []);
  if (appliedFilters.branchOfficeIds?.length) {
    result.branchOffices = getBranchOffices(appliedFilters.branchOfficeIds, allBranchOffices);
  }

  if (appliedFilters.departmentIds?.length) {
    result.departments = getDepartments(appliedFilters.departmentIds, allBranchOffices);
  }

  if (!appliedFilters.company) {
    return result;
  }
  if (!filterData?.companies) {
    return result;
  }
  const companyFilter = filterData.companies.find((c) => c.companyId === appliedFilters.company);

  if (!companyFilter) {
    return result;
  }

  const company: AutocompleteOption = {
    value: companyFilter.companyId,
    label: companyFilter.companyName,
  };
  result.company = company;

  if (!appliedFilters.branchOfficeIds) {
    return result;
  }

  const branchOfficeFilter = companyFilter.branchOffices.find((bo) =>
    appliedFilters.branchOfficeIds?.includes(bo.branchOfficeId)
  );

  if (!branchOfficeFilter) {
    return result;
  }

  if (!appliedFilters.departmentIds) {
    return result;
  }

  const departmentFilter = branchOfficeFilter.departments.find((dept) =>
    appliedFilters.departmentIds?.includes(dept.departmentId)
  );

  if (!departmentFilter) {
    return result;
  }

  return result;
};
